import { useEffect, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type CSSProperties, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { DEFAULT_USER_AVATAR, UODEX_COLORS as C } from '@/constants';

type ThemedPanelTheme = {
  border?: string;
  glassBg?: string;
  glassShadow?: string;
};

export type ThemedPanelProps = HTMLAttributes<HTMLElement> & {
  theme?: ThemedPanelTheme;
};

export function ThemedPanel({ children, className = '', theme, style, ...props }: ThemedPanelProps) {
  const panelTheme = theme ?? { border: C.border, glassBg: C.glassBg };

  return (
    <section
      {...props}
      className={cn('relative overflow-hidden rounded-xl border backdrop-blur-[2px]', className)}
      style={{ borderColor: panelTheme.border, background: panelTheme.glassBg, boxShadow: panelTheme.glassShadow, ...style }}
    >
      {children}
    </section>
  );
}

export function ThemedInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'h-10 w-full rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 text-base font-semibold outline-none placeholder:text-white/50 focus:border-[#ADD741] transition-colors',
        props.className,
      )}
      style={{ ...props.style }}
    />
  );
}

export function ThemedTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        'w-full resize-none rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 py-2 text-base font-semibold outline-none placeholder:text-white/50 focus:border-[#ADD741] transition-colors',
        props.className,
      )}
      style={{ ...props.style }}
    />
  );
}

type ThemedSelectOption<T extends string> = { value: T; label: string; disabled?: boolean };

type ThemedDropdownSelectProps<T extends string> = {
  value: T;
  options: ThemedSelectOption<T>[];
  open: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onToggle: () => void;
  onSelect: (value: T) => void;
};

type ThemedNativeSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  options?: never;
  open?: never;
  onToggle?: never;
  onSelect?: never;
};

function ThemedSelectChevron({ up }: { up: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} className={cn(up && 'rotate-180')}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}

function ThemedDropdownSelect<T extends string>({ value, options, open, disabled, placeholder, className = '', onToggle, onSelect }: ThemedDropdownSelectProps<T>) {
  const selected = options.find((option) => option.value === value);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<{ left: number; top: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setMenuStyle(null);
      return;
    }

    const updateMenuPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuStyle({ left: rect.left, top: rect.bottom + 8, width: rect.width });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  const menu = open && menuStyle && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="fixed z-[9999] max-h-56 overflow-y-auto rounded-xl border border-[#F5EC63]/40 bg-[#07100A]/95 p-1 shadow-[0_10px_28px_rgba(0,0,0,0.55),0_0_18px_rgba(245,236,99,0.20)] backdrop-blur-md"
        style={{ left: menuStyle.left, top: menuStyle.top, width: menuStyle.width }}
      >
        {options.map((option) => {
          const active = option.value === value;

          return (
            <button
              key={option.value}
              type="button"
              disabled={option.disabled}
              className={cn(
                'flex h-10 w-full items-center rounded-lg px-3 text-left text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                active ? 'bg-[#ADD741]/20 text-[#F5EC63]' : 'text-[#E5E7EB] hover:bg-[#F5EC63]/10',
              )}
              onClick={() => onSelect(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>,
      document.body,
    )
    : null;

  return (
    <div className={cn('relative z-[80] flex flex-col', className)}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 text-left text-base font-semibold text-[#E5E7EB] outline-none transition-colors focus:border-[#ADD741] disabled:cursor-not-allowed disabled:opacity-60',
        )}
        onClick={onToggle}
      >
        <span className="truncate">{selected?.label ?? placeholder ?? '—'}</span>
        <ThemedSelectChevron up={open} />
      </button>
      {menu}
    </div>
  );
}

export function ThemedSelect<T extends string>(props: ThemedDropdownSelectProps<T> | ThemedNativeSelectProps) {
  if ('options' in props && props.options) {
    return <ThemedDropdownSelect {...props} />;
  }

  return (
    <select
      {...props}
      className={cn('h-10 w-full rounded-lg border px-3 text-sm font-semibold text-white outline-none focus:border-[#ADD741]', props.className)}
      style={{ borderColor: C.softBorder, background: C.inputBg, ...props.style }}
    />
  );
}

export function ThemedField({ label, required, headerRight, children }: { label: string | ReactNode; required?: boolean; headerRight?: ReactNode; children: ReactNode }) {
  return (
    <label className={cn('flex flex-col gap-2')}>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <span className="shrink-0 text-sm leading-none" style={{ color: C.muted }}>
          {label} {required ? <span style={{ color: C.sell }}>*</span> : null}
        </span>
        {headerRight ? <div className="min-w-0 flex-1 text-right">{headerRight}</div> : null}
      </div>
      {children}
    </label>
  );
}

export function ThemedNotice({ tone, children }: { tone: 'success' | 'error' | 'info' | 'warning'; children: ReactNode }) {
  const color = tone === 'success' ? C.buy : tone === 'error' ? C.sell : tone === 'warning' ? '#F5A623' : C.gold;
  return (
    <div
      className="rounded-xl border px-4 py-3 text-sm transition-colors"
      style={{ borderColor: `${color}55`, background: `${color}1f`, color }}
    >
      {children}
    </div>
  );
}

export function ThemedSubmitButton({ children, disabled, onClick, type = 'button', className = '', style, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cn('h-11 w-full rounded-xl border text-sm font-semibold transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60', className)}
      style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text, ...style }}
    >
      {children}
    </button>
  );
}

export function ThemedMetric({ label, value, color = C.text }: { label: string; value: ReactNode; color?: string }) {
  return (
    <div className="min-w-0" style={{ background: C.inputBg }}>
      <div className="truncate text-[10px]" style={{ color: C.muted }}>
        {label}
      </div>
      <div className="mt-1 truncate font-mono text-sm font-semibold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export function ThemedTabButton({ active, children, onClick }: { active: boolean; children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('relative flex-1 overflow-hidden h-9 text-[11px] font-semibold transition-all active:scale-[0.98]')}
      style={{ color: active ? C.gold : C.muted }}
    >
      {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
      <span className="relative block truncate">{children}</span>
    </button>
  );
}

export function ThemedChip({ active, children }: { active?: boolean; children: ReactNode }) {
  return (
    <span
      className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors')}
      style={{ borderColor: active ? `${C.gold}66` : C.softBorder, background: active ? C.chipBg : C.inputBg, color: active ? C.gold : C.muted }}
    >
      {children}
    </span>
  );
}

export function ThemedAvatar({ src, name, seed: _seed, className = 'h-9 w-9' }: { src?: string | null; name?: string; seed?: string; className?: string }) {
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setUseFallback(false);
  }, [src]);

  const resolvedSrc = src?.trim() && !useFallback ? src.trim() : DEFAULT_USER_AVATAR;

  return (
    <img
      src={resolvedSrc}
      alt={name ?? ''}
      width={36}
      height={36}
      loading="lazy"
      decoding="async"
      className={cn('rounded-full border object-cover', className)}
      style={{ borderColor: C.softBorder, background: C.chipBg }}
      onError={() => {
        if (resolvedSrc !== DEFAULT_USER_AVATAR) setUseFallback(true);
      }}
    />
  );
}

export function ThemedStatusBadge({ status, label, tone }: { status?: string; label?: string; tone?: 'blue' | 'green' | 'red' | 'info' | 'muted' }) {
  const displayLabel = label || status;
  let color: string = C.muted;
  if (tone === 'blue') color = C.blue;
  else if (tone === 'green' || (status === 'done' || status === 'filled')) color = C.buy;
  else if (tone === 'red' || (status === 'dispute')) color = C.sell;
  else if (tone === 'info' || (status === 'partial' || status === 'open')) color = '#F5A623';
  
  return (
    <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium" style={{ borderColor: `${color}55`, background: `${color}1f`, color }}>
      {displayLabel}
    </span>
  );
}

export function ThemedTabStrip<T extends string = string>({ tabs, value, onChange }: { tabs: { key: T; label: string }[]; value: T; onChange: (value: T) => void }) {
  return (
    <div className="flex w-full gap-1">
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-9 flex-1 overflow-hidden px-1 text-[11px] font-semibold transition-all active:scale-[0.98]"
            style={{
              color: active ? C.gold : C.muted,
            }}
          >
            {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
            <span className="relative block truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ThemedFixedTabStrip<T extends string = string>({ tabs, value, onChange, top = '56px', background = '#000000' }: { tabs: { key: T; label: string }[]; value: T; onChange: (value: T) => void; top?: string; background?: string }) {
  return (
    <div className="fixed left-1/2 z-40 w-full max-w-[430px] -translate-x-1/2 px-5 py-1" style={{ top, background }}>
      <ThemedTabStrip tabs={tabs} value={value} onChange={onChange} />
    </div>
  );
}

export function ThemedFixedTopBar({
  children,
  top = '56px',
  background = '#000000',
  className = '',
  style,
}: {
  children: ReactNode;
  top?: string;
  background?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn('fixed left-1/2 z-40 w-full max-w-[430px] -translate-x-1/2 px-5 py-2', className)}
      style={{ top, background, ...style }}
    >
      {children}
    </div>
  );
}

export function ThemedBottomActionBar({ children, background = 'rgba(0,0,0,0.85)', className = '', style }: { children: ReactNode; background?: string; className?: string; style?: CSSProperties }) {
  return (
    <div className={cn('fixed bottom-0 left-1/2 z-40 w-full max-w-[430px] -translate-x-1/2 px-5 pb-[calc(16px+env(safe-area-inset-bottom))] pt-4 backdrop-blur-md', className)} style={{ background, ...style }}>
      {children}
    </div>
  );
}

export { C as THEMED_SURFACE_COLORS };
